import { camelizeKeys } from 'humps';

import AuthenticationStore from '~/auth/authenticationStore';
import { ssoDebugLog } from '~/auth/ssoDebugLog';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { intlService } from '~/i18n/IntlService';
import { authExt, pilot } from '~/services/serviceInstances';
import { ERROR } from '~/types/toast.types';

import { getClientFromUrl } from '../routing/utils';

export function redirectToDomainLogin(): void {
  window.location.href = '/';
}

export function redirectToClientHome(): void {
  const client = getClientFromUrl();
  window.location.href = client ? `/${client}` : '/';
}

export function redirectToLogin(client: string): void {
  window.location.href = `/${client}/login`;
}

export function redirectToLoginEmbed(): void {
  const client = getClientFromUrl();
  window.location.href = `/${client}/login/embed`;
}

export function redirectToFolder(
  subdomain: string,
  folderId: string | number,
): void {
  window.location.href = `${window.location.origin}/${subdomain}/documents/${folderId}`;
}

export function redirectToPage(subdomain: string, path: string): void {
  window.location.href = `${window.location.origin}/${subdomain}/${path}`;
}

interface ZendeskResponse {
  error?: string;
  method: string;
  zendesk_url?: string;
  jwt_string?: string;
  return_to?: string;
  redirect_url?: string;
}

export async function redirectToZendesk(
  zendeskRedirectUrl: string,
): Promise<void> {
  try {
    const response = (await authExt.get(
      `/zendesk/sso?return_to=${zendeskRedirectUrl}`,
    )) as ZendeskResponse;
    if (response.error) throw response.error;

    if (response.method === 'post') {
      AuthenticationStore().removeZendeskFlag();
      const form = document.createElement('form');
      form.method = response.method;
      form.action = response.zendesk_url!;

      const jwt = document.createElement('input');
      jwt.type = 'hidden';
      jwt.name = 'jwt';
      jwt.value = response.jwt_string!;
      form.appendChild(jwt);

      const returnTo = document.createElement('input');
      returnTo.type = 'text';
      returnTo.name = 'return_to';
      returnTo.value = response.return_to!;
      form.appendChild(returnTo);

      document.body.appendChild(form);
      form.submit();
    } else {
      AuthenticationStore().removeZendeskFlag();
      window.location.href = response.redirect_url!;
    }
  } catch {
    showToast(
      ERROR,
      intlService.getIntl().formatMessage({
        id: 'redirects.zendeskRedirectError',
        defaultMessage: 'Error on fetching redirect URL',
        description: 'Error message when fetching Zendesk redirect URL fails',
      }),
    );
  }
}

interface OAuthResponse {
  error?: string;
  redirect_url: string;
}

export async function redirectToOAuth(
  redirectUri: string,
  clientId: string,
  state: string,
): Promise<void> {
  ssoDebugLog('redirectToOAuth called', { redirectUri, clientId, state });
  try {
    const response = (await authExt.get(
      `/oauth/redirect?redirect_uri=${redirectUri}&client_id=${clientId}&state=${state}`,
    )) as OAuthResponse;

    ssoDebugLog('Got OAuth redirect response', {
      redirectUrl: response.redirect_url,
      hasError: !!response.error,
    });

    if (response.error) throw response.error;

    AuthenticationStore().removeAegisOAuthProxy();
    ssoDebugLog('Navigating to OAuth callback', {
      redirectUrl: response.redirect_url,
    });
    window.location.href = response.redirect_url;
  } catch (err) {
    ssoDebugLog('redirectToOAuth FAILED', { error: String(err) });
    showToast(
      ERROR,
      intlService.getIntl().formatMessage({
        id: 'redirects.oauthRedirectError',
        defaultMessage: 'Error on fetching redirect URL',
        description: 'Error message when fetching OAuth redirect URL fails',
      }),
    );
  }
}

interface ProviderAuthPageResponse {
  returnTo: string;
  provider: string;
  syncPairId: string;
}

// Sync-pair state tokens are opaque, backend-issued identifiers. Restricting
// to this charset rejects path traversal sequences (e.g. `../`) before the
// value is interpolated into the API path.
const SYNC_PAIR_STATE_PATTERN = /^[\w-]+$/;

function isSafeReturnToUrl(returnTo: string): boolean {
  try {
    const url = new URL(returnTo, window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export async function redirectToProviderAuthPage(
  state: string,
  code: string,
): Promise<void> {
  try {
    if (!SYNC_PAIR_STATE_PATTERN.test(state)) {
      throw new Error('Invalid sync-pair state');
    }

    const resp = await pilot.get(`/sync-pair/state/${state}/`);

    const { returnTo, provider, syncPairId } = camelizeKeys(
      resp,
    ) as unknown as ProviderAuthPageResponse;

    if (!isSafeReturnToUrl(returnTo)) {
      throw new Error('Invalid return_to URL');
    }

    const params = btoa(JSON.stringify({ provider, state, code, syncPairId }));

    const delimiter = returnTo.match(/\?./) ? '&' : '?';
    window.location.href = `${returnTo}${delimiter}states=${params}`;
  } catch {
    showToast(
      ERROR,
      intlService.getIntl().formatMessage({
        id: 'redirects.providerAuthError',
        defaultMessage: 'Error on fetching provider auth state data',
        description:
          'Error message when fetching provider auth state data fails',
      }),
    );
  }
}

export function getAbsoluteRedirectUrl(
  windowOrigin: string,
  clientName: string,
  redirectPath: { pathname?: string; search?: string } | undefined,
): string {
  return `${windowOrigin}/${clientName}${redirectPath?.pathname}${redirectPath?.search}`;
}
